/**
 * Output Footer Sections — jsPDF block layout.
 *
 * pdf.ts is hand-drawn (no HTML renderer), so a section's HTML is converted to
 * blocks (sectionHtml.ts) and laid out here with the same page metrics and
 * partner colours the rest of the PDF uses. Every block renderer goes through
 * one `Layout` cursor that knows about page breaks and carries the section's
 * fit scale.
 *
 * Page controls:
 *   startOnNewPage — addPage() unless the cursor is already at the top of a page.
 *   maxPages       — FIT the whole section into N pages: dry-run at 100%, then
 *                    at a uniformly smaller scale (text, spacing, images and
 *                    tables together, in 5% steps down to FIT_MIN_SCALE) until
 *                    it fits. Nothing is ever cut; if even the floor won't fit,
 *                    the section is emitted at the floor and runs long.
 *   breakAfter     — addPage() after the section so the disclaimer starts fresh.
 *
 * The dry run reuses the exact renderers with `emit = false`: the only doc calls
 * they make in that mode are font setters and text measurement, which have no
 * visible side effects. Every renderer sizes through `L.pt()` / `L.mm()`, which
 * is what makes the fit scale uniform.
 */

import type { jsPDF } from 'jspdf';
import type { OutputSection } from '../config/outputSections';
import { htmlToBlocks, type ImageBlock, type InlineRun, type SectionBlock, type TableCell } from './sectionHtml';

export interface PdfImage { dataUrl: string; w: number; h: number; format?: 'PNG' | 'JPEG' }

/** jsPDF stores PNGs uncompressed unless told otherwise — always deflate. */
const IMAGE_COMPRESSION = 'MEDIUM' as const;

export interface PdfSectionCtx {
  doc: jsPDF;
  margin: number;
  pageWidth: number;
  pageHeight: number;
  colors: { primary: string; secondary: string; gray800: string; gray600: string; gray400: string; lightGray: string; white: string };
  headerBg: string;
  headerText: string;
  /** Brand primary for a table's title band — what the screen's --color-primary resolves to. */
  titleBg: string;
  titleFg: string;
  borderColor: string;
  stripeBg: string;
  getY(): number;
  setY(y: number): void;
  /** Cursor may not pass this (pageHeight - footer reserve). */
  bottomLimit: number;
  newPage(): void;
  loadImage(url: string): Promise<PdfImage | null>;
}

const PT_TO_MM = 25.4 / 72;
const lh = (pt: number) => pt * 1.15 * PT_TO_MM;
const PX_TO_MM = 25.4 / 96;
const BODY_PT = 9;
const CELL_PAD = 1.6;
const FILL_IN_ROW_MM = 9;
/** Trusscore product gray, for a header cell that names the GRAY colour group. */
const SWATCH_GRAY = '#D0D3D4';
/** Fit-to-pages: try 100%, then step down by FIT_STEP to FIT_MIN_SCALE. */
export const FIT_MIN_SCALE = 0.5;
const FIT_STEP = 0.05;

class Layout {
  y: number;
  pagesUsed = 1;
  constructor(
    readonly ctx: PdfSectionCtx,
    readonly emit: boolean,
    startY: number,
    /** Uniform content scale (1 = authored size); see maxPages. */
    readonly scale: number,
  ) { this.y = startY; }

  get width(): number { return this.ctx.pageWidth - 2 * this.ctx.margin; }
  get x(): number { return this.ctx.margin; }
  get atTop(): boolean { return this.y <= this.ctx.margin + 0.01; }
  /** A font size at the current scale. */
  pt(base: number): number { return base * this.scale; }
  /** A spacing / padding at the current scale. */
  mm(base: number): number { return base * this.scale; }

  /** Ensure `h` mm fit below the cursor, breaking the page if needed. Returns true if it broke. */
  need(h: number): boolean {
    if (this.y + h <= this.ctx.bottomLimit) return false;
    if (this.atTop) return false; // a block taller than a page draws from the top regardless
    this.pagesUsed++;
    if (this.emit) this.ctx.newPage(); else this.ctx.setY(this.ctx.margin);
    this.y = this.ctx.margin;
    return true;
  }
}

// ── Public entry ─────────────────────────────────────────────────────────────

export async function drawOutputSections(sections: OutputSection[], ctx: PdfSectionCtx): Promise<void> {
  const pdfSections = sections
    .filter((s) => s.content.display.pdf && s.content.html.trim() !== '')
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  if (pdfSections.length === 0) return;

  const parsed = pdfSections.map((s) => ({ section: s, blocks: htmlToBlocks(s.content.html) }));

  // Pre-fetch every image once; a failure draws a placeholder, never aborts.
  const images = new Map<string, PdfImage | null>();
  const srcs = new Set<string>();
  parsed.forEach(({ blocks }) => blocks.forEach((b) => {
    if (b.type === 'image') srcs.add(b.src);
    if (b.type === 'imageRow') b.images.forEach((i) => srcs.add(i.src));
  }));
  await Promise.all(Array.from(srcs).map(async (src) => {
    try { images.set(src, await ctx.loadImage(src)); } catch { images.set(src, null); }
  }));

  parsed.forEach(({ section, blocks }) => {
    const page = section.content.page;
    if (page.startOnNewPage && ctx.getY() > ctx.margin + 0.01) ctx.newPage();

    // Fit to a page limit: scale the whole section down until it fits. Never cut.
    let scale = 1;
    if (page.maxPages) {
      const startY = ctx.getY();
      for (let s = 1; s >= FIT_MIN_SCALE - 1e-9; s = Math.round((s - FIT_STEP) * 100) / 100) {
        const dry = new Layout(ctx, false, startY, s);
        layoutSection(dry, section, blocks, images);
        ctx.setY(startY);
        scale = s;
        if (dry.pagesUsed <= page.maxPages) break;
      }
    }

    const live = new Layout(ctx, true, ctx.getY(), scale);
    layoutSection(live, section, blocks, images);
    ctx.setY(live.y);

    if (page.breakAfter) ctx.newPage();
  });
}

// ── Section ──────────────────────────────────────────────────────────────────

function layoutSection(L: Layout, section: OutputSection, blocks: SectionBlock[], images: Map<string, PdfImage | null>): void {
  drawTitle(L, section.title);
  blocks.forEach((b, i) => drawBlock(L, b, images, blocks[i + 1]));
  L.y += L.mm(4);
}

function drawTitle(L: Layout, title: string): void {
  if (!title.trim()) return;
  const { doc, colors } = L.ctx;
  const pt = L.pt(12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(pt);
  const lines: string[] = doc.splitTextToSize(title, L.width);
  const h = lines.length * lh(pt) + L.mm(7);
  L.need(h + L.mm(12)); // keep the title with at least a line of content
  if (L.emit) {
    doc.setTextColor(colors.gray800);
    lines.forEach((ln) => { L.y += lh(pt); doc.text(ln, L.x, L.y - L.mm(1.2)); });
    doc.setDrawColor(colors.primary);
    doc.setLineWidth(0.6);
    doc.line(L.x, L.y + L.mm(1), L.x + L.width, L.y + L.mm(1));
  } else {
    L.y += lines.length * lh(pt);
  }
  L.y += L.mm(6);
}

function drawBlock(L: Layout, b: SectionBlock, images: Map<string, PdfImage | null>, next?: SectionBlock): void {
  switch (b.type) {
    case 'heading': return drawHeading(L, b.level, b.runs, next);
    case 'paragraph': return drawParagraph(L, b.runs, b.align, L.pt(BODY_PT), L.ctx.colors.gray800, L.width, L.x, L.mm(2.5));
    case 'list': return drawList(L, b.ordered, b.items);
    case 'image': return drawImageRow(L, [b], images);
    case 'imageRow': return drawImageRow(L, b.images, images);
    case 'table': return drawTable(L, b.hasHeaderRow, b.rows);
    case 'hr': {
      L.need(L.mm(6));
      if (L.emit) { L.ctx.doc.setDrawColor(L.ctx.colors.gray400); L.ctx.doc.setLineWidth(0.2); L.ctx.doc.line(L.x, L.y + L.mm(2), L.x + L.width, L.y + L.mm(2)); }
      L.y += L.mm(6);
      return;
    }
    case 'spacer': L.y += L.mm(b.height); return;
  }
}

// ── Text ─────────────────────────────────────────────────────────────────────

interface Token { text: string; run: InlineRun; w: number }
interface Line { tokens: Token[]; w: number; }

const fontStyle = (r: InlineRun) => (r.bold && r.italic ? 'bolditalic' : r.bold ? 'bold' : r.italic ? 'italic' : 'normal');

function setRunFont(doc: jsPDF, r: InlineRun, pt: number, forceBold = false): void {
  doc.setFont('helvetica', forceBold && !r.italic ? 'bold' : forceBold ? 'bolditalic' : fontStyle(r));
  doc.setFontSize(pt);
}

/** Run-aware greedy word wrap (jsPDF's splitTextToSize is single-font). */
function wrapRuns(doc: jsPDF, runs: InlineRun[], pt: number, maxW: number, forceBold = false): Line[] {
  const lines: Line[] = [];
  let cur: Line = { tokens: [], w: 0 };
  const push = () => { lines.push(cur); cur = { tokens: [], w: 0 }; };

  runs.forEach((run) => {
    if (run.br) { push(); return; }
    setRunFont(doc, run, pt, forceBold);
    const words = run.text.match(/\S+\s*|\s+/g) ?? [];
    words.forEach((word) => {
      let w = doc.getTextWidth(word);
      // A single word wider than the column: hard-split it.
      if (w > maxW) {
        let rest = word;
        while (rest.length > 1 && doc.getTextWidth(rest) > maxW) {
          let cut = rest.length - 1;
          while (cut > 1 && doc.getTextWidth(rest.slice(0, cut)) > maxW) cut--;
          const piece = rest.slice(0, cut);
          if (cur.tokens.length) push();
          cur.tokens.push({ text: piece, run, w: doc.getTextWidth(piece) });
          cur.w = doc.getTextWidth(piece);
          push();
          rest = rest.slice(cut);
        }
        word = rest; w = doc.getTextWidth(word);
      }
      const trailing = word.replace(/\s+$/, '');
      const wTrim = trailing === word ? w : doc.getTextWidth(trailing);
      if (cur.tokens.length && cur.w + wTrim > maxW) push();
      if (cur.tokens.length === 0 && word.trim() === '') return; // no leading whitespace on a line
      cur.tokens.push({ text: word, run, w });
      cur.w += w;
    });
  });
  if (cur.tokens.length || lines.length === 0) push();
  // Trailing whitespace never counts toward alignment.
  lines.forEach((ln) => {
    const last = ln.tokens[ln.tokens.length - 1];
    if (last && /\s$/.test(last.text)) {
      const trimmed = last.text.replace(/\s+$/, '');
      setRunFont(doc, last.run, pt, forceBold);
      const tw = doc.getTextWidth(trimmed);
      ln.w -= last.w - tw;
      last.text = trimmed; last.w = tw;
    }
  });
  return lines.filter((ln) => ln.tokens.length > 0 || lines.length === 1);
}

function drawLine(L: Layout, line: Line, pt: number, color: string, x0: number, maxW: number, align: 'left' | 'center' | 'right', forceBold = false): void {
  const { doc, colors } = L.ctx;
  let x = x0;
  if (align === 'center') x = x0 + (maxW - line.w) / 2;
  else if (align === 'right') x = x0 + (maxW - line.w);
  line.tokens.forEach((t) => {
    setRunFont(doc, t.run, pt, forceBold);
    if (t.run.href) {
      doc.setTextColor(colors.secondary);
      doc.textWithLink(t.text, x, L.y, { url: t.run.href });
      doc.setDrawColor(colors.secondary);
      doc.setLineWidth(0.2);
      doc.line(x, L.y + 0.7, x + t.w, L.y + 0.7);
    } else {
      doc.setTextColor(color);
      doc.text(t.text, x, L.y);
    }
    x += t.w;
  });
}

/** Wrapped, mixed-style text. `gapAfter` mm follows the last line. */
function drawParagraph(L: Layout, runs: InlineRun[], align: 'left' | 'center' | 'right', pt: number, color: string, maxW: number, x0: number, gapAfter: number, forceBold = false): void {
  const lines = wrapRuns(L.ctx.doc, runs, pt, maxW, forceBold);
  const step = lh(pt);
  lines.forEach((line, i) => {
    // Keep at least two lines together at a page end (no orphan first line).
    L.need(i === 0 ? Math.min(lines.length, 2) * step : step);
    L.y += step;
    if (L.emit) drawLine(L, line, pt, color, x0, maxW, align, forceBold);
  });
  L.y += gapAfter;
}

function drawHeading(L: Layout, level: 2 | 3 | 4, runs: InlineRun[], next?: SectionBlock): void {
  const pt = L.pt(level === 2 ? 11 : level === 3 ? 10 : 9);
  const color = level === 4 ? L.ctx.colors.gray800 : L.ctx.colors.primary;
  const lines = wrapRuns(L.ctx.doc, runs, pt, L.width, true);
  // Keep-with-next: the heading plus a first line of whatever follows.
  const follow = next && next.type !== 'heading' ? lh(L.pt(BODY_PT)) * 2 : 0;
  L.need(lines.length * lh(pt) + L.mm(3) + follow);
  L.y += L.mm(1.5);
  drawParagraph(L, runs, 'left', pt, color, L.width, L.x, L.mm(1.5), true);
}

function drawList(L: Layout, ordered: boolean, items: { runs: InlineRun[]; depth: number }[]): void {
  const { doc, colors } = L.ctx;
  const pt = L.pt(BODY_PT);
  const counters: number[] = [];
  items.forEach((item) => {
    const indent = L.mm(4 + item.depth * 5);
    counters.length = item.depth + 1;
    counters[item.depth] = (counters[item.depth] ?? 0) + 1;
    const marker = ordered ? `${counters[item.depth]}.` : '•';
    const x0 = L.x + indent;
    const before = L.y;
    const firstLineY = { v: 0 };
    // Draw the text, then the marker on its first line.
    const lines = wrapRuns(doc, item.runs, pt, L.width - indent);
    lines.forEach((line, i) => {
      L.need(i === 0 ? Math.min(lines.length, 2) * lh(pt) : lh(pt));
      L.y += lh(pt);
      if (i === 0) firstLineY.v = L.y;
      if (L.emit) drawLine(L, line, pt, colors.gray800, x0, L.width - indent, 'left');
    });
    if (L.emit && lines.length) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(pt); doc.setTextColor(colors.gray800);
      doc.text(marker, x0 - L.mm(ordered ? 5 : 3.2), firstLineY.v);
    }
    L.y += L.mm(1);
    if (L.y === before) L.y += lh(pt);
  });
  L.y += L.mm(1.5);
}

// ── Images ───────────────────────────────────────────────────────────────────

function drawImageRow(L: Layout, imgs: ImageBlock[], images: Map<string, PdfImage | null>): void {
  const { doc, colors } = L.ctx;
  const n = imgs.length;
  const gap = L.mm(4);
  const capPt = L.pt(8);
  const cellW = (L.width - gap * (n - 1)) / n;
  const bodyMax = L.ctx.bottomLimit - L.ctx.margin - 24;

  // Size each image inside its cell (never upscale past natural size), at the
  // section's fit scale.
  const sized = imgs.map((img) => {
    const meta = images.get(img.src) ?? null;
    const naturalW = meta ? meta.w * PX_TO_MM : cellW;
    const naturalH = meta ? meta.h * PX_TO_MM : cellW * 0.6;
    let w = Math.min(cellW * (n === 1 ? img.widthHint : 1), naturalW) * L.scale;
    w = Math.max(Math.min(w, cellW), Math.min(cellW, L.mm(30)));
    let h = w * (naturalH / naturalW);
    if (h > bodyMax) { h = bodyMax; w = h * (naturalW / naturalH); }
    const capLines = img.caption ? wrapRuns(doc, img.caption, capPt, w) : [];
    return { img, meta, w, h, capLines };
  });
  const rowH = Math.max(...sized.map((s) => s.h));
  const capH = Math.max(0, ...sized.map((s) => s.capLines.length * lh(capPt) + (s.capLines.length ? L.mm(1.5) : 0)));
  L.need(rowH + capH + L.mm(4));

  if (L.emit) {
    let x = L.x;
    sized.forEach((s) => {
      const ix = x + (cellW - s.w) / 2;
      if (s.meta) {
        doc.addImage(s.meta.dataUrl, s.meta.format ?? 'PNG', ix, L.y, s.w, s.h, undefined, IMAGE_COMPRESSION);
      } else {
        // Missing image: dashed box with the alt text.
        doc.setDrawColor(colors.gray400); doc.setLineWidth(0.3);
        doc.setLineDashPattern([1.2, 1.2], 0);
        doc.rect(ix, L.y, s.w, s.h);
        doc.setLineDashPattern([], 0);
        doc.setFont('helvetica', 'italic'); doc.setFontSize(capPt); doc.setTextColor(colors.gray600);
        const alt: string[] = doc.splitTextToSize(s.img.alt || 'Image unavailable', s.w - 4);
        doc.text(alt.slice(0, 3), ix + s.w / 2, L.y + s.h / 2, { align: 'center' });
      }
      // Caption under the image, centred.
      let cy = L.y + rowH + L.mm(1.5);
      s.capLines.forEach((line) => {
        cy += lh(capPt);
        const save = L.y; L.y = cy;
        drawLine(L, line, capPt, colors.gray600, ix, s.w, 'center');
        L.y = save;
      });
      x += cellW + gap;
    });
  }
  L.y += rowH + capH + L.mm(4);
}

// ── Tables ───────────────────────────────────────────────────────────────────

/**
 * Column widths from content: each column wants its longest unbroken word at
 * least, and its longest line up to a cap (so a wide worksheet heading like
 * "Quantity (perimeter ÷ 10)" earns room without a paragraph-length cell
 * hogging the table). Spanning cells don't vote. Then scale to the page width.
 */
function tableColumnWidths(doc: jsPDF, rows: { cells: TableCell[] }[], totalCols: number, width: number, pt: number, cellPad: number): number[] {
  const MIN_MM = 8;
  const CAP_MM = 55;
  const pad = 2 * cellPad + 0.8;
  // min: the longest single word in the column (never split a word if it fits).
  // pref: the longest line up to a cap (what the column would like).
  const min: number[] = new Array(totalCols).fill(MIN_MM);
  const pref: number[] = new Array(totalCols).fill(MIN_MM);
  rows.forEach((r) => {
    let col = 0;
    r.cells.forEach((c) => {
      if (c.colSpan === 1 && !c.spanned && col < totalCols) {
        let line = 0; let longestLine = 0; let longestWord = 0;
        c.runs.forEach((run) => {
          if (run.br) { longestLine = Math.max(longestLine, line); line = 0; return; }
          setRunFont(doc, run, pt, c.header);
          line += doc.getTextWidth(run.text);
          run.text.split(/\s+/).forEach((w) => { if (w) longestWord = Math.max(longestWord, doc.getTextWidth(w)); });
        });
        longestLine = Math.max(longestLine, line);
        min[col] = Math.max(min[col], longestWord + pad);
        pref[col] = Math.max(pref[col], Math.min(longestLine, CAP_MM) + pad);
      }
      col += c.colSpan;
    });
  });
  for (let i = 0; i < totalCols; i++) pref[i] = Math.max(pref[i], min[i]);

  const minSum = min.reduce((a, b) => a + b, 0);
  if (minSum >= width) return min.map((w) => (w * width) / minSum); // can't avoid splitting: shrink evenly
  // Hand out the spare width in proportion to how much each column still wants.
  const extra = width - minSum;
  const desire = pref.map((p, i) => p - min[i]);
  const desireSum = desire.reduce((a, b) => a + b, 0);
  if (desireSum <= 0) return min.map((w) => w + extra / totalCols);
  if (desireSum <= extra) {
    // Everyone gets their preference; the remainder is shared evenly.
    const left = (extra - desireSum) / totalCols;
    return pref.map((p) => p + left);
  }
  return min.map((w, i) => w + (extra * desire[i]) / desireSum);
}

/** Mix two hex colours: t = 0 → a, t = 1 → b. */
function mixHex(a: string, b: string, t: number): string {
  const p = (h: string) => {
    const s = h.replace('#', '');
    const f = s.length === 3 ? s.split('').map((ch) => ch + ch).join('') : s;
    return [0, 2, 4].map((i) => parseInt(f.slice(i, i + 2), 16) || 0);
  };
  const A = p(a); const B = p(b);
  return '#' + A.map((v, i) => Math.round(v + (B[i] - v) * t).toString(16).padStart(2, '0')).join('');
}

function drawTable(L: Layout, _hasHeaderRow: boolean, rows: { cells: TableCell[] }[]): void {
  const { doc, colors, headerBg, headerText, titleBg, titleFg, borderColor, stripeBg } = L.ctx;
  const totalCols = Math.max(1, ...rows.map((r) => r.cells.reduce((n, c) => n + c.colSpan, 0)));
  const pt = L.pt(8.5);
  const step = lh(pt);
  const CELL_PAD_S = L.mm(CELL_PAD);
  const fillInRowMm = L.mm(FILL_IN_ROW_MM);

  // Leading all-<th> rows form the header block: drawn together, repeated
  // after a page break (a grouped worksheet header is two or three rows).
  let headerCount = 0;
  while (headerCount < rows.length && rows[headerCount].cells.every((c) => c.header)) headerCount++;
  const centredHeads = headerCount > 1; // grouped headers read as a worksheet: centre them
  // A title band — the first row is one header cell spanning every column —
  // takes the brand primary (the original printed worksheet's orange bar).
  const hasTitleBand = headerCount > 0 && rows[0].cells.length === 1 && rows[0].cells[0].colSpan >= totalCols;

  const colW = tableColumnWidths(doc, rows, totalCols, L.width, pt, CELL_PAD_S);
  const xs: number[] = [L.x];
  colW.forEach((w, i) => { xs[i + 1] = xs[i] + w; });

  // One border colour and weight for the whole grid. Dividers INSIDE a dark
  // band are a lighter tint of that band so they don't read as gray scratches;
  // the table's outer frame is drawn last, on top, in the body border colour.
  const GRID_W = 0.25;
  const headDivider = mixHex(headerBg, '#ffffff', 0.28);
  const titleDivider = mixHex(titleBg, '#ffffff', 0.28);
  const spanW = (col: number, n: number) => xs[Math.min(totalCols, col + n)] - xs[Math.min(totalCols, col)];

  const measure = (r: { cells: TableCell[] }, header: boolean) => {
    let col = 0;
    const cells = r.cells.map((c) => {
      const w = spanW(col, c.colSpan) - 2 * CELL_PAD_S;
      const lines = c.spanned ? [] : wrapRuns(doc, c.runs, pt, Math.max(1, w), header || c.header);
      const empty = c.runs.every((x) => x.text.trim() === '');
      const out = { c, col, lines: empty ? [] : lines, w, empty };
      col += c.colSpan;
      return out;
    });
    const textH = Math.max(1, ...cells.map((x) => x.lines.length)) * step + 2 * CELL_PAD_S;
    const real = cells.filter((x) => !x.c.spanned);
    const fillIn = !header && real.some((x) => x.empty) && real.some((x) => !x.empty);
    return { cells, h: fillIn ? Math.max(textH, fillInRowMm) : textH, fillIn, header };
  };
  const measured = rows.map((r, i) => measure(r, i < headerCount));

  /** Height of rows i .. i+n-1 (a rowspan's box), clipped to the table. */
  const spanH = (i: number, n: number) => {
    let h = 0;
    for (let j = i; j < Math.min(measured.length, i + n); j++) h += measured[j].h;
    return h;
  };
  /** Vertical room a row needs to start: its own height, or its tallest rowspan. */
  const needH = (i: number) => Math.max(measured[i].h, ...measured[i].cells.map((x) => (x.c.rowSpan > 1 ? spanH(i, x.c.rowSpan) : 0)));

  const drawRow = (i: number, stripe: boolean) => {
    const r = measured[i];
    if (!L.emit) { L.y += r.h; return; }
    r.cells.forEach(({ c, col, lines, empty }) => {
      if (c.spanned) return; // the cell above drew this slot
      const x = xs[Math.min(totalCols, col)];
      const w = spanW(col, c.colSpan);
      const h = c.rowSpan > 1 ? spanH(i, c.rowSpan) : r.h;
      const isHead = r.header || c.header;
      const isTitle = hasTitleBand && i === 0;
      // Every cell in a row shares one background: a worksheet (fill-in) row is
      // all white so the boxes and their labels read as one row; other body
      // rows stripe as a whole.
      // A header cell naming a product colour (WHITE / GRAY) is drawn AS that colour.
      const swatch = isHead && !isTitle ? c.swatch : undefined;
      if (isTitle) { doc.setFillColor(titleBg); doc.rect(x, L.y, w, h, 'F'); }
      else if (swatch) { doc.setFillColor(swatch === 'gray' ? SWATCH_GRAY : colors.white); doc.rect(x, L.y, w, h, 'F'); }
      else if (isHead) { doc.setFillColor(headerBg); doc.rect(x, L.y, w, h, 'F'); }
      else if (stripe && !r.fillIn) { doc.setFillColor(stripeBg); doc.rect(x, L.y, w, h, 'F'); }
      // Cell border: one weight everywhere; tinted inside the dark bands.
      doc.setDrawColor(isTitle ? titleDivider : isHead && !swatch ? headDivider : borderColor);
      doc.setLineWidth(GRID_W);
      doc.rect(x, L.y, w, h);
      // Text is vertically centred in its box, header and body alike.
      const textH = lines.length * step;
      const save = L.y;
      L.y = save + (h - textH) / 2;
      const align = isHead && (centredHeads || c.colSpan > 1) ? 'center' : 'left';
      const textColor = isTitle ? titleFg : swatch ? colors.gray800 : isHead ? headerText : colors.gray800;
      lines.forEach((line) => {
        L.y += step;
        drawLine(L, line, pt, textColor, x + CELL_PAD_S, w - 2 * CELL_PAD_S, align, isHead);
      });
      L.y = save;
    });
    L.y += r.h;
  };
  const drawHeader = () => { for (let i = 0; i < headerCount; i++) drawRow(i, false); };
  /** Outer frame over the rows drawn since `top`, so the edge is one clean line. */
  const frame = (top: number) => {
    if (!L.emit || L.y <= top) return;
    doc.setDrawColor(borderColor);
    doc.setLineWidth(GRID_W);
    doc.rect(L.x, top, L.width, L.y - top);
  };

  L.y += L.mm(1);
  // Keep the header block with its first two body rows (no lone row before a break).
  L.need(spanH(0, headerCount) + spanH(headerCount, 2) + L.mm(2));
  let top = L.y;
  drawHeader();
  for (let i = headerCount; i < measured.length; i++) {
    const before = L.y;
    if (L.need(needH(i) + L.mm(1))) {
      // Close the frame on the page we left, then repeat the header block.
      const save = L.y; L.y = before; frame(top); L.y = save;
      top = L.y;
      drawHeader();
    }
    drawRow(i, (i - headerCount) % 2 === 1);
  }
  frame(top);
  L.y += L.mm(4);
}
